import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { Button, Modal } from 'antd';

import CreatePaymentConditionModal from '@/components/Financial/Accounts/PaymentConditions/CardAdministrators/CreatePaymentConditionsModal';
import UpdatePaymentConditionModal from '@/components/Financial/Accounts/PaymentConditions/CardAdministrators/UpdatePaymentConditionsModal';
import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import { useDeletePaymentCondition } from '@/core/domain/enterprise/PaymentCondition/PaymentCondition.hooks';
import { PageProps } from '@/types/PageProps';
import {
  ListPaymentConditionsQuery,
  PaymentCondition,
} from '@/core/domain/enterprise/PaymentCondition/PaymentCondition.types';
import { useDebounce, useDisclosure } from '@/hooks';
import PaymentsConditionList from '@/components/Financial/Accounts/PaymentConditions/PaymentsConditionList';
import PaymentsConditionFilters from '@/components/Financial/Accounts/PaymentConditions/PaymentsConditionFilters';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { hasPermissions } from '@/utils/functions/hasPermissions';

const PaymentConditionsPage = ({ route }: PageProps) => {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useState<ListPaymentConditionsQuery>({
    order: 'ASC',
    ...convertParams<ListPaymentConditionsQuery>({
      order: 'string',
      name: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams);
  usePersistSearchParams(queryDebounce);

  const [createModal, createModalAction] = useDisclosure();
  const [updateModal, updateModalAction] = useDisclosure();

  const [selectedPaymentId, setSelectedPaymentCondition] = useState<number>();

  const removeMutation = useDeletePaymentCondition();

  const handleUpdateModalVisible = (paymentId?: number) => {
    if (!updateModal) {
      setSelectedPaymentCondition(paymentId);
    }

    updateModalAction.toggle();
  };

  const handleRemoveClick = (paymentId: PaymentCondition) => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'paymentConditions.remove' }),
      content: `${intl.formatMessage({
        id: 'paymentConditions.remove.alert',
      })} ${paymentId?.name}`,

      onOk: async () => {
        await removeMutation.mutateAsync(paymentId.id);
      },

      okType: 'danger',
    });
  };

  return (
    <DefaultRouteTabs
      permissions={route.permissions}
      persistSearchParams={false}
      route={route.parent}
      extra={
        <Button
          type="primary"
          block
          onClick={() => createModalAction.open()}
          disabled={
            !hasPermissions([Authorities.REGISTER_PAYMENT_CONDITIONS_ADD])
          }
        >
          {intl.formatMessage({ id: 'paymentConditions.create' })}
        </Button>
      }
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <PaymentsConditionFilters
        queryParams={queryParams}
        setQueryParams={setQueryParams}
      />

      <PaymentsConditionList
        params={queryDebounce}
        update={handleUpdateModalVisible}
        remove={handleRemoveClick}
      />

      <CreatePaymentConditionModal
        visible={createModal}
        handleVisible={() => createModalAction.toggle()}
      />

      <UpdatePaymentConditionModal
        visible={updateModal}
        handleVisible={handleUpdateModalVisible}
        paymentId={selectedPaymentId}
      />
    </DefaultRouteTabs>
  );
};

export default PaymentConditionsPage;
